import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { useEffect } from 'react';
import { Checkbox, Labeled } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IFileUploaderValue } from '~/components/organisms/fields/FileUploader';
import type { IContainerProps } from '~/components/utils/Container';
import type { IFormMode } from '~/helpers/types';
import { FormField } from '~/components/atoms/FormField';
import { FormBadgesSection } from '~/components/formSections/FormBadgesSection';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { usePersistentForm } from '~/components/utils/PersistentForm';
import { useFormAutoFocus } from '~/hooks/useFormAutoFocus';
import { LinkFormMainSection } from './LinkFormMainSection';

export type ILinkFormValues = {
  name?: string;
  pictures?: Array<IFileUploaderValue>;
  parentId?: string;
  actionType?: string;
  actionPrefix?: string;
  actionTarget?: string;
  actionTargetCategoryId?: string;
  isDraft?: boolean;
  userBadges?: Array<string>;
  requireAllUserBadges?: boolean;
};

export type ILinkFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<ILinkFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting'
  > & {
    mode: IFormMode;
    appId: string;
    categoryId?: string;
    defaultValues?: DefaultValues<ILinkFormValues>;
    onSubmit?: SubmitHandler<ILinkFormValues>;
    onDraftStatusChange?: (isDraft: boolean) => void;
  };

export const LinkForm: React.FC<ILinkFormProps> = (props) => {
  const {
    sx,
    mode,
    appId,
    categoryId,
    defaultValues,
    onSubmit,
    onDraftStatusChange,
    ...other
  } = props;

  const { t } = useTranslation();
  const form = usePersistentForm<ILinkFormValues>({ defaultValues });
  useFormAutoFocus({ form, name: 'name' });

  const isDraft = form.watch('isDraft');
  useEffect(() => {
    onDraftStatusChange?.(!!isDraft);
  }, [onDraftStatusChange, isDraft]);

  return (
    <ContentBlock.FormBody
      sx={sx}
      form={form}
      onSubmit={onSubmit}
      extraActions={
        <Labeled label={t('LinkForm.field.draft.label')} labelPosition="right">
          <FormField name="isDraft" control={form.control} as={Checkbox} />
        </Labeled>
      }
      {...other}
    >
      <LinkFormMainSection
        form={form}
        mode={mode}
        appId={appId}
        categoryId={categoryId}
      />
      <FormBadgesSection form={form} mode={mode} appId={appId} />
    </ContentBlock.FormBody>
  );
};
